import React, { useState, useEffect } from 'react';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassSelect } from '../ui/GlassSelect';
import { apiClient } from '../../services/api';
import { Save, RefreshCw } from 'lucide-react';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


// Must mirror VALID_GEMINI_MODELS in agents.py — the backend rejects anything
// not in that list, so an option here with no counterpart there fails on save.
// gemini-2.0-flash / 1.5-* removed: retired, selecting them guaranteed a failure.
const MODEL_OPTIONS = [
  { value: 'gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash Lite (recommended — fast, lowest cost)' },
  { value: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash Lite (cheapest)' },
  { value: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash (higher quality, ~5x cost)' },
  { value: 'gemini-3.6-flash', label: 'Gemini 3.6 Flash (highest quality)' },
  { value: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro (reasoning, preview)' },
  { value: 'gemini-3.1-flash-lite-preview', label: 'Gemini 3.1 Flash Lite (low latency, preview)' },
  { value: 'gemini-3-flash-preview', label: 'Gemini 3 Flash (fast, preview)' },
  { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash (deprecated — price rise Jan 2027)' },
  { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite (deprecated)' },
  { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro (deprecated)' },
];

const AGENT_OPTIONS = [
  { value: 'gemini', label: 'Gemini (with fallback)' },
  { value: 'gemini_simple', label: 'Gemini Simple (single model)' },
];

interface LLMConfig {
  primary_model: string;
  fallback_models: string[];
  default_agent: string;
}

const PlatformConfigPanel: React.FC = () => {
  const [config, setConfig] = useState<LLMConfig>({
    // Placeholder only — overwritten by the GET /settings/llm-config response
    // on mount. Kept in step with the backend defaults to avoid a flash of
    // stale/retired model ids before load completes.
    primary_model: 'gemini-2.5-flash',
    fallback_models: ['gemini-3.1-flash-lite'],
    default_agent: 'gemini',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/settings/platform/llm-config');
      setConfig(res.data);
    } catch (err: any) {
      setMessage({ type: 'error', text: getApiError(err, 'Failed to load LLM config') });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadConfig(); }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      const res = await apiClient.put('/settings/platform/llm-config', config);
      setMessage({ type: 'success', text: res.data.message || 'Saved' });
    } catch (err: any) {
      setMessage({ type: 'error', text: getApiError(err, 'Failed to save') });
    } finally {
      setSaving(false);
    }
  };

  const updateFallback = (index: number, value: string) => {
    const updated = [...config.fallback_models];
    updated[index] = value;
    setConfig({ ...config, fallback_models: updated });
  };

  if (loading) {
    return (
      <GlassCard padding="lg">
        <p>Loading LLM configuration...</p>
      </GlassCard>
    );
  }

  return (
    <div style={{ maxWidth: 600 }}>
      <GlassCard padding="lg">
        <h3 style={{ margin: '0 0 1.5rem', color: 'var(--text)' }}>LLM Model Configuration</h3>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
          Configure which Gemini models are used for AI generation. Changes take effect immediately.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <GlassSelect
            label="Primary Model"
            options={MODEL_OPTIONS}
            value={config.primary_model}
            onChange={(value) => setConfig({ ...config, primary_model: value })}
          />

          <GlassSelect
            label="Fallback Model 1"
            options={MODEL_OPTIONS}
            value={config.fallback_models[0] || ''}
            onChange={(value) => updateFallback(0, value)}
          />

          <GlassSelect
            label="Fallback Model 2"
            options={MODEL_OPTIONS}
            value={config.fallback_models[1] || ''}
            onChange={(value) => updateFallback(1, value)}
          />

          <GlassSelect
            label="Default Agent"
            options={AGENT_OPTIONS}
            value={config.default_agent}
            onChange={(value) => setConfig({ ...config, default_agent: value })}
          />
        </div>

        {message && (
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem 1rem',
            borderRadius: 'var(--radius-md)',
            background: message.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            color: message.type === 'success' ? 'var(--success, #10b981)' : 'var(--danger, #ef4444)',
            fontSize: '0.875rem',
          }}>
            {message.text}
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
          <GlassButton
            variant="primary"
            onClick={handleSave}
            disabled={saving}
            icon={<Save size={16} />}
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </GlassButton>
          <GlassButton
            variant="secondary"
            onClick={loadConfig}
            icon={<RefreshCw size={16} />}
          >
            Reset
          </GlassButton>
        </div>
      </GlassCard>
    </div>
  );
};

export default PlatformConfigPanel;
